import { useState, useEffect } from 'react';
import { X, Plus, Trash2, AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';
import ConfirmDialog from '../common/ConfirmDialog';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import edgeService from '../../services/edge.service';

// VSNP-733: 'permit_reflect' is the VPP stateful-permit action (action=2).
// On TAP-bridge edges the agent uses iptables conntrack to achieve the same
// outcome, so the UI hides the option there (see edgeMode in the modal).
type ACLAct = 'permit' | 'deny' | 'permit_reflect';

interface ACLRule {
  id?: string;
  act: ACLAct;
  sip: string;
  sip_mask: string;
  dip: string;
  dip_mask: string;
  sport: string;
  dport: string;
  protocol: number;
  sort_order: number;
}

interface ACLData {
  id?: string;
  name: string;
  interfaces?: string[];
  direction?: 'inbound' | 'outbound'; // VSNP-1011: bind arc (inbound=ingress, outbound=egress)
  priority?: number; // VSNP-968: cross-ACL evaluation order (lower = first)
  aclSessionList: ACLRule[];
}

interface InterfaceOption {
  name: string;
  // VSNP-733: 'role' lets us render WAN/LAN/VPN badges so users can see at
  // a glance which interface is the upstream — important before clicking
  // 'Deny ALL' on a WAN port.
  type: 'physical' | 'wireguard';
  role?: 'wan' | 'lan' | 'wireguard';
  enabled: boolean;
}

interface ACLModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  edgeGuid: string;
  existingAcl?: ACLData | null;
  // VSNP-1011: initial direction for a NEW ACL (e.g. the active tab). Ignored
  // when editing — the existing ACL's own direction wins.
  defaultDirection?: 'inbound' | 'outbound';
}

const PROTOCOLS = [
  { value: 0, label: 'Any' },
  { value: 1, label: 'ICMP' },
  { value: 6, label: 'TCP' },
  { value: 17, label: 'UDP' },
];

const emptyRule: ACLRule = {
  act: 'permit',
  sip: '',
  sip_mask: '',
  dip: '',
  dip_mask: '',
  sport: '',
  dport: '',
  protocol: 0,
  sort_order: 0,
};

// VSNP-733: shared CIDR/IP validators. Mirror the API Zod regexes so client
// and server agree on what's valid. Returns true for empty (the field is
// optional) so unfilled fields don't trigger spurious errors.
const ipv4Re = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/;
const maskBitsRe = /^([0-9]|[12][0-9]|3[0-2])$/;
const aclNameRe = /^[a-zA-Z0-9_-]+$/;

const isValidIPv4 = (v: string): boolean => v === '' || ipv4Re.test(v);

// VSNP-733: detect IANA private / loopback / CGNAT / link-local ranges. The
// "Use my IP" affordance is meant to fill the SOURCE IP that the edge will
// see when the operator's traffic reaches it — that's a public IP. If the
// echo service returns a private address it means the user's browser path
// to the echo is going through a NAT-ing tunnel (corporate VPN, iPhone
// tether NAT chain) and the result is unusable for an edge-ACL rule.
const isPrivateIPv4 = (v: string): boolean => {
  if (!ipv4Re.test(v)) return false;
  const parts = v.split('.').map(Number);
  const [a, b] = parts;
  if (a === 10) return true;                          // 10.0.0.0/8
  if (a === 172 && b >= 16 && b <= 31) return true;   // 172.16.0.0/12
  if (a === 192 && b === 168) return true;            // 192.168.0.0/16
  if (a === 127) return true;                         // 127.0.0.0/8 loopback
  if (a === 169 && b === 254) return true;            // 169.254.0.0/16 link-local
  if (a === 100 && b >= 64 && b <= 127) return true;  // 100.64.0.0/10 CGNAT
  if (a === 0) return true;                           // 0.0.0.0/8
  return false;
};
const isValidMask = (v: string): boolean => v === '' || maskBitsRe.test(v);
const isValidPort = (v: string): boolean => {
  if (v === '' || v === '*') return true;
  const n = parseInt(v, 10);
  return !isNaN(n) && n >= 0 && n <= 65535;
};

const ACLModal = ({ isOpen, onClose, onSave, edgeGuid, existingAcl, defaultDirection = 'inbound' }: ACLModalProps) => {
  const [name, setName] = useState('');
  const [rules, setRules] = useState<ACLRule[]>([{ ...emptyRule }]);
  const [selectedInterfaces, setSelectedInterfaces] = useState<string[]>([]);
  const [allInterfaces, setAllInterfaces] = useState(false);
  // VSNP-968: cross-ACL evaluation order on the interface (lower = first).
  const [priority, setPriority] = useState(100);
  const [availableInterfaces, setAvailableInterfaces] = useState<InterfaceOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [loadingInterfaces, setLoadingInterfaces] = useState(false);
  // VSNP-733: structured errors map for inline form errors. Keys are
  // 'name' or 'rule.<i>.<field>'. Mirrors RuleModal pattern.
  const [errors, setErrors] = useState<Record<string, string>>({});
  // VSNP-733: when the interface fetch fails outright, we surface a banner
  // and disable Save rather than falling back to a hardcoded [G0,G1,G2]
  // list that didn't apply to TAP-bridge edges anyway.
  const [interfacesLoadError, setInterfacesLoadError] = useState<string | null>(null);
  // VSNP-733: edge mode drives which action options to show. On TAP-bridge
  // edges the agent uses iptables conntrack for stateful filtering, so
  // 'permit_reflect' is irrelevant — hide it to avoid confusion.
  const [edgeMode, setEdgeMode] = useState<'tap-bridge' | 'dpdk' | 'af_packet' | 'unknown'>('unknown');
  // VSNP-733: stylised confirmation for "deny on WAN" — replaces the
  // native window.confirm() since the rest of the UI uses ConfirmDialog
  // for destructive actions.
  const [denyOnWanConfirmOpen, setDenyOnWanConfirmOpen] = useState(false);
  // VSNP-1011: per-ACL bind direction. Inbound = VPP input arc (ingress),
  // Outbound = output arc (egress). Outbound cannot target WAN ports — that
  // arc is owned by the system WAN guard (sys-wan-guard) — so WAN interfaces
  // are hidden when Outbound is selected, and the API rejects it too.
  const [direction, setDirection] = useState<'inbound' | 'outbound'>('inbound');

  // VSNP-1011: the interfaces a user may actually bind to, given the direction.
  // WAN ports are not selectable for Outbound ACLs.
  const selectableInterfaces = availableInterfaces.filter(
    (i) => direction !== 'outbound' || i.role !== 'wan',
  );

  // Fetch available interfaces + edge mode when modal opens
  useEffect(() => {
    if (isOpen && edgeGuid) {
      fetchAvailableInterfaces();
      fetchEdgeMode();
    }
  }, [isOpen, edgeGuid]);

  const fetchEdgeMode = async () => {
    try {
      const resp = await api.get(`/api/v2/edges/${edgeGuid}`);
      const mode = resp?.data?.data?.vpp_mode || resp?.data?.vpp_mode;
      if (typeof mode === 'string') {
        const lower = mode.toLowerCase();
        if (lower.includes('tap')) setEdgeMode('tap-bridge');
        else if (lower.includes('af_packet') || lower.includes('afpacket')) setEdgeMode('af_packet');
        else if (lower.includes('dpdk')) setEdgeMode('dpdk');
        else setEdgeMode('unknown');
      }
    } catch (err) {
      // Non-fatal — fall back to showing all options.
      console.warn('ACLModal: could not determine edge mode', err);
      setEdgeMode('unknown');
    }
  };

  const fetchAvailableInterfaces = async () => {
    setInterfacesLoadError(null);
    try {
      setLoadingInterfaces(true);
      const physicalResponse = await edgeService.getPhysicalInterfaces(edgeGuid);
      const physicalInterfaces: InterfaceOption[] = (physicalResponse.items || [])
        .filter((iface: any) => iface.configuredState === true)
        .map((iface: any) => ({
          name: iface.name,
          type: 'physical' as const,
          role: iface.role === 'wan' ? 'wan' as const
              : iface.role === 'lan' ? 'lan' as const
              : undefined,
          enabled: iface.operationalState === 'Up' || iface.configuredState,
        }));

      // VSNP-1010: WireGuard interfaces are NOT static — the peering tunnel is
      // wgN (wg2 for a single peering, wg12 for a second slot…), and wg0/wg1
      // exist only when the IoT / App VPN gateways are enabled. Derive the real
      // inventory from the edge instead of hardcoding wg0/wg1 (which offered
      // non-existent interfaces and hid the actual tunnel). Non-fatal: physical
      // interfaces still load if the WG lookup fails.
      let wgInterfaces: InterfaceOption[] = [];
      try {
        const wgResponse = await edgeService.getWireguardInterfaces(edgeGuid);
        wgInterfaces = (wgResponse.items || []).map((wg: any) => ({
          name: wg.name,
          type: 'wireguard' as const,
          role: 'wireguard' as const,
          enabled: wg.enabled !== false,
        }));
      } catch (wgError) {
        console.warn('ACLModal: could not load WireGuard interfaces', wgError);
      }

      setAvailableInterfaces([...physicalInterfaces, ...wgInterfaces]);
    } catch (error) {
      // VSNP-733: previously fell back to a hardcoded [G0,G1,G2] list. That
      // didn't match TAP-bridge edges (which have tap500/tap501 internally)
      // and silently led to ACLs that bound nothing. Now we surface the
      // error and require the operator to retry — no silent fallback.
      console.error('Failed to fetch interfaces:', error);
      setAvailableInterfaces([]);
      setInterfacesLoadError('Unable to load interfaces from edge. Check that the edge is online and try again.');
    } finally {
      setLoadingInterfaces(false);
    }
  };

  // VSNP-733: fetch the operator's PUBLIC IP for the "Use my IP" affordance.
  //
  // The earlier implementation called our own /api/v2/whoami which returns
  // the IP the API ingress sees. When the operator is on a corporate VPN or
  // tethered through a NAT chain (e.g. iPhone hotspot), that path can land
  // a private 10.x.x.x at our ingress — completely useless as a source IP
  // for an edge ACL rule, since the EDGE will see the operator's true
  // public IP, not the VPN/tether private.
  //
  // Strategy:
  //   1. Try a public IPv4-only echo service (api.ipify.org) first. This is
  //      what any internet endpoint sees the operator as — same view the
  //      edge has. Fast, no auth, IPv4-only by design.
  //   2. If that fails (corporate firewall blocks ipify, offline, etc.),
  //      fall back to our /api/v2/whoami.
  //   3. If the resulting IP is in a private / CGNAT / link-local range,
  //      refuse to fill the field — show a warning so the operator knows
  //      to find their real public IP some other way (their ISP, the
  //      `curl ifconfig.me` trick, etc.) rather than silently writing a
  //      bogus rule.
  const fetchAndFillMyIp = async (index: number) => {
    let ip: string | undefined;
    let source = 'unknown';
    let lastErr: string | null = null;

    // Step 1: public echo (no auth, no cookies)
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 5000);
      const r = await fetch('https://api.ipify.org?format=json', {
        signal: ctrl.signal,
        credentials: 'omit',
      });
      clearTimeout(t);
      if (r.ok) {
        const data = await r.json();
        if (data?.ip && ipv4Re.test(data.ip)) {
          ip = data.ip;
          source = 'ipify';
        } else {
          lastErr = 'ipify returned non-IPv4';
        }
      } else {
        lastErr = `ipify HTTP ${r.status}`;
      }
    } catch (e: any) {
      lastErr = e?.name === 'AbortError' ? 'ipify timeout' : (e?.message || 'ipify failed');
    }

    // Step 2: fall back to our endpoint
    if (!ip) {
      try {
        const resp = await api.get('/api/v2/whoami');
        const candidate: string | undefined =
          resp?.data?.data?.ip ||
          resp?.data?.ip;
        if (candidate && ipv4Re.test(candidate)) {
          ip = candidate;
          source = 'api-whoami';
        }
      } catch (err: any) {
        const detail = err?.response?.data?.message || err?.response?.statusText || err?.message || 'fetch failed';
        lastErr = `${lastErr ? lastErr + '; ' : ''}whoami ${detail}`;
      }
    }

    if (!ip) {
      const msg = lastErr || 'no IPv4 returned';
      setErrors((prev) => ({ ...prev, [`rule.${index}.sip`]: `Could not detect your IP: ${msg}` }));
      toast.error(`Use my IP failed: ${msg}`);
      return;
    }

    if (isPrivateIPv4(ip)) {
      // Refuse to fill. A private IP here means the path to the echo
      // service is going through an upstream NAT (VPN / tether) that
      // hides the operator's real public source. Writing that into an
      // edge ACL would be wrong — the edge sees a totally different IP.
      const msg = `Detected ${ip} (private/CGNAT). You're behind a VPN or tether NAT — find your public IP another way and enter it manually.`;
      setErrors((prev) => ({ ...prev, [`rule.${index}.sip`]: msg }));
      toast.error(msg);
      return;
    }

    setRules((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], sip: ip!, sip_mask: '32' };
      return next;
    });
    setErrors((prev) => {
      const n = { ...prev };
      delete n[`rule.${index}.sip`];
      delete n[`rule.${index}.sip_mask`];
      return n;
    });
    toast.success(`Filled source IP with ${ip}/32 (${source})`);
  };

  useEffect(() => {
    if (existingAcl) {
      setName(existingAcl.name);
      setPriority(existingAcl.priority ?? 100); // VSNP-968
      setDirection(existingAcl.direction === 'outbound' ? 'outbound' : 'inbound'); // VSNP-1011
      setRules(existingAcl.aclSessionList.length > 0 ? existingAcl.aclSessionList : [{ ...emptyRule }]);
      // Parse existing interfaces
      const existingInterfaces = existingAcl.interfaces || [];
      if (existingInterfaces.length === 0) {
        setAllInterfaces(false);
        setSelectedInterfaces([]);
      } else {
        // Check if all available interfaces are selected
        const enabledInterfaceNames = availableInterfaces.filter(i => i.enabled).map(i => i.name);
        const allSelected = enabledInterfaceNames.length > 0 &&
          enabledInterfaceNames.every(name => existingInterfaces.includes(name));
        setAllInterfaces(allSelected);
        setSelectedInterfaces(existingInterfaces);
      }
    } else {
      setName('');
      setPriority(100); // VSNP-968
      setDirection(defaultDirection); // VSNP-1011: seed from the active tab
      setRules([{ ...emptyRule }]);
      setSelectedInterfaces([]);
      setAllInterfaces(false);
    }
  }, [existingAcl, isOpen, availableInterfaces, defaultDirection]);

  // VSNP-1011: switching to Outbound drops any already-selected WAN interface,
  // since outbound ACLs cannot bind to a WAN port (guard owns that arc).
  const handleDirectionChange = (dir: 'inbound' | 'outbound') => {
    setDirection(dir);
    if (dir === 'outbound') {
      const wanNames = new Set(availableInterfaces.filter((i) => i.role === 'wan').map((i) => i.name));
      setSelectedInterfaces((prev) => prev.filter((n) => !wanNames.has(n)));
      setAllInterfaces(false);
    }
  };

  const handleAllInterfacesChange = (checked: boolean) => {
    setAllInterfaces(checked);
    if (checked) {
      // Select all enabled, selectable interfaces (excludes WAN when outbound).
      const enabledInterfaceNames = selectableInterfaces.filter(i => i.enabled).map(i => i.name);
      setSelectedInterfaces(enabledInterfaceNames);
    } else {
      setSelectedInterfaces([]);
    }
  };

  const handleInterfaceToggle = (interfaceName: string, checked: boolean) => {
    if (checked) {
      const newSelected = [...selectedInterfaces, interfaceName];
      setSelectedInterfaces(newSelected);
      // Check if all selectable interfaces are now selected
      const enabledInterfaceNames = selectableInterfaces.filter(i => i.enabled).map(i => i.name);
      setAllInterfaces(enabledInterfaceNames.every(name => newSelected.includes(name)));
    } else {
      setSelectedInterfaces(selectedInterfaces.filter(i => i !== interfaceName));
      setAllInterfaces(false);
    }
  };

  const handleAddRule = () => {
    setRules([...rules, { ...emptyRule, sort_order: rules.length }]);
  };

  const handleRemoveRule = (index: number) => {
    if (rules.length > 1) {
      setRules(rules.filter((_, i) => i !== index));
    }
  };

  const handleRuleChange = (index: number, field: keyof ACLRule, value: any) => {
    const newRules = [...rules];
    newRules[index] = { ...newRules[index], [field]: value };
    setRules(newRules);
    // Clear inline error for the touched field as the user types — same
    // pattern as RuleModal: keep red borders only until the next edit.
    setErrors((prev) => {
      if (!(`rule.${index}.${field}` in prev)) return prev;
      const next = { ...prev };
      delete next[`rule.${index}.${field}`];
      return next;
    });
  };

  // VSNP-733: in-modal client-side reorder via up/down arrows. Mirrors
  // PolicyDetailsModal.tsx handleMoveRule. sort_order is rewritten from
  // the array index at save time, so no extra API roundtrip is needed.
  const handleMoveRule = (index: number, direction: 'up' | 'down') => {
    setRules((prev) => {
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  // VSNP-733: full client-side validation. Returns an errors map; empty
  // map = OK. Same pattern as RuleModal — surface errors inline rather
  // than via toast so users can see exactly which field is wrong.
  const validate = (): Record<string, string> => {
    const out: Record<string, string> = {};
    if (!name.trim()) {
      out['name'] = 'ACL name is required';
    } else if (!aclNameRe.test(name.trim())) {
      out['name'] = 'Only letters, digits, dash and underscore are allowed';
    } else if (name.trim().length > 64) {
      out['name'] = 'Name must be 64 characters or fewer';
    }

    rules.forEach((rule, i) => {
      if (rule.sip && !isValidIPv4(rule.sip)) {
        out[`rule.${i}.sip`] = 'Must be a valid IPv4 address';
      }
      if (rule.dip && !isValidIPv4(rule.dip)) {
        out[`rule.${i}.dip`] = 'Must be a valid IPv4 address';
      }
      if (rule.sip_mask && !isValidMask(rule.sip_mask)) {
        out[`rule.${i}.sip_mask`] = 'Mask must be 0-32';
      }
      if (rule.dip_mask && !isValidMask(rule.dip_mask)) {
        out[`rule.${i}.dip_mask`] = 'Mask must be 0-32';
      }
      // /0 with a specific source/dest IP almost always means the user
      // meant /32. Block this before submit to match the API refiner.
      if (rule.sip_mask === '0' && rule.sip && rule.sip !== '0.0.0.0') {
        out[`rule.${i}.sip_mask`] = 'Use /32 for a single host (0 only valid with 0.0.0.0)';
      }
      if (rule.dip_mask === '0' && rule.dip && rule.dip !== '0.0.0.0') {
        out[`rule.${i}.dip_mask`] = 'Use /32 for a single host (0 only valid with 0.0.0.0)';
      }
      if (!isValidPort(rule.sport)) {
        out[`rule.${i}.sport`] = 'Port must be 0-65535 or blank';
      }
      if (!isValidPort(rule.dport)) {
        out[`rule.${i}.dport`] = 'Port must be 0-65535 or blank';
      }
    });

    // VSNP-973: unattached ACLs are allowed (define now / attach later). We no
    // longer block save on an empty interface list — instead the ACL list shows
    // configured vs actual on-edge bindings so an unbound ACL is visible.
    return out;
  };

  // VSNP-733: Deny-ALL banner. The ACL plugin enforces an implicit deny at
  // the end on DPDK (VPP-ACL semantics). A rule list that contains only
  // permit-style rules thus blocks everything else — including return
  // traffic for unrelated connections. Show an amber warning so users
  // understand the implication before saving.
  const onlyPermits = rules.length > 0 && rules.every((r) => r.act === 'permit' || r.act === 'permit_reflect');
  // VSNP-733: extra confirm when the user adds a `deny` rule bound to a
  // WAN interface — typical lock-yourself-out scenario.
  const wanInterfaceNames = new Set(availableInterfaces.filter((i) => i.role === 'wan').map((i) => i.name));
  const denyOnWan = rules.some((r) => r.act === 'deny') && selectedInterfaces.some((n) => wanInterfaceNames.has(n));

  const handleSave = async () => {
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      // Don't close the modal; the inline errors tell the user what to fix.
      return;
    }
    setErrors({});

    if (denyOnWan) {
      // Show stylised ConfirmDialog (same component the rest of the UI
      // uses for destructive actions). Submission resumes in submitSave()
      // once the user confirms.
      setDenyOnWanConfirmOpen(true);
      return;
    }

    await submitSave();
  };

  const submitSave = async () => {
    try {
      setSaving(true);
      const payload = {
        name: name.trim(),
        interfaces: selectedInterfaces,
        direction, // VSNP-1011: inbound (ingress) | outbound (egress)
        priority, // VSNP-968: cross-ACL evaluation order

        rules: rules.map((rule, index) => ({
          ...rule,
          sort_order: index,
          sip: rule.sip || '0.0.0.0',
          dip: rule.dip || '0.0.0.0',
          // VSNP-733: blank mask now defaults to '32' (single host) on
          // the wire when an IP is present. The Zod refiner accepts /0
          // only with the wildcard IP, matching this behaviour.
          sip_mask: rule.sip_mask || (rule.sip ? '32' : '0'),
          dip_mask: rule.dip_mask || (rule.dip ? '32' : '0'),
        })),
      };

      if (existingAcl?.id) {
        await api.put(`/api/v2/edges/${edgeGuid}/acl/${existingAcl.id}`, payload);
        toast.success('ACL updated successfully');
      } else {
        await api.post(`/api/v2/edges/${edgeGuid}/acl`, payload);
        toast.success('ACL created successfully');
      }

      onSave();
      onClose();
    } catch (error: any) {
      console.error('Failed to save ACL:', error);
      // Surface field-specific errors from API when possible.
      const detail = error?.response?.data?.error || error?.response?.data?.message;
      toast.error(detail || 'Failed to save ACL');
    } finally {
      setSaving(false);
    }
  };

  // VSNP-733: hide permit_reflect on TAP-bridge edges (stateful filtering
  // is provided by iptables conntrack there, not VPP).
  const showReflect = edgeMode !== 'tap-bridge';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {existingAcl ? 'Edit ACL' : 'Create New ACL'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-6">
          {/* ACL Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ACL Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors((prev) => { const n = { ...prev }; delete n.name; return n; });
              }}
              className={`input-base w-full ${errors.name ? 'border-red-300 focus:ring-red-500' : ''}`}
              placeholder="e.g., allow-web-traffic"
            />
            {errors.name && (
              <p className="text-xs text-red-600 flex items-center mt-1">
                <AlertTriangle className="w-3 h-3 mr-1" />{errors.name}
              </p>
            )}
          </div>

          {/* VSNP-968: priority is set by reordering ACLs (↑/↓) in the ACL
              list, not typed here — mirrors rule ordering. `priority` is still
              carried in the payload so an existing ACL keeps its position. */}

          {/* VSNP-1011: bind direction. Inbound applies on the VPP input arc
              (traffic entering the interface); Outbound on the output arc
              (traffic leaving it). Outbound cannot target WAN ports. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Direction</label>
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
              {(['inbound', 'outbound'] as const).map((dir) => (
                <button
                  key={dir}
                  type="button"
                  onClick={() => handleDirectionChange(dir)}
                  className={`px-4 py-1.5 text-sm font-medium capitalize focus:outline-none ${
                    direction === dir
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {dir}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {direction === 'inbound'
                ? 'Filters traffic entering the selected interface(s).'
                : 'Filters traffic leaving the selected interface(s). WAN ports are managed by the system guard and cannot be selected.'}
            </p>
          </div>

          {/* Interfaces */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Apply to Interfaces
            </label>
            {loadingInterfaces ? (
              <div className="text-sm text-gray-500">Loading interfaces...</div>
            ) : interfacesLoadError ? (
              // VSNP-733: replaces the silent fallback to [G0,G1,G2]. The
              // operator must acknowledge that the edge is unreachable
              // rather than the modal pretending the edge has GX names.
              <div className="bg-red-50 border border-red-300 rounded-lg p-3 flex items-start space-x-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-800">{interfacesLoadError}</p>
                  <button
                    type="button"
                    onClick={fetchAvailableInterfaces}
                    className="text-xs text-blue-600 underline mt-1"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {/* All Interfaces checkbox */}
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={allInterfaces}
                    onChange={(e) => handleAllInterfacesChange(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">All Interfaces</span>
                </label>

                {/* Individual interface checkboxes. VSNP-1011: WAN ports are
                    omitted for Outbound ACLs (guard owns that egress arc). */}
                <div className="ml-6 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {selectableInterfaces.map((iface) => {
                    // VSNP-733: role badges (WAN / LAN / VPN). Helps the
                    // user spot WAN ports — denying on them is the most
                    // common way to lock oneself out of an edge.
                    const badge = iface.role === 'wan' ? { text: 'WAN', cls: 'bg-amber-100 text-amber-800' }
                                : iface.role === 'lan' ? { text: 'LAN', cls: 'bg-blue-100 text-blue-800' }
                                : iface.role === 'wireguard' ? { text: 'VPN', cls: 'bg-purple-100 text-purple-800' }
                                : null;
                    return (
                      <label key={iface.name} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedInterfaces.includes(iface.name)}
                          onChange={(e) => handleInterfaceToggle(iface.name, e.target.checked)}
                          disabled={!iface.enabled}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                        />
                        <span className={`text-sm ${!iface.enabled ? 'text-gray-400' : 'text-gray-700'}`}>
                          {iface.name}
                          {badge && (
                            <span className={`text-[10px] uppercase tracking-wide ml-1 px-1 py-0.5 rounded ${badge.cls}`}>
                              {badge.text}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>

                {selectableInterfaces.length === 0 && (
                  <p className="text-sm text-gray-500">
                    {direction === 'outbound' && availableInterfaces.length > 0
                      ? 'No non-WAN interfaces available for an outbound ACL.'
                      : 'No interfaces available'}
                  </p>
                )}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Select at least one interface to apply this ACL to. An ACL not attached to an interface has no effect.
            </p>
            {/* VSNP-973 */}
            {errors.interfaces && (
              <p className="text-xs text-red-600 flex items-center mt-1">
                <AlertTriangle className="w-3 h-3 mr-1" />{errors.interfaces}
              </p>
            )}
          </div>

          {/* VSNP-733: Deny-ALL implicit warning. ACL is enforced by VPP
              with an implicit deny at the bottom of the list (DPDK) or by
              iptables with an explicit default DROP at the bottom of the
              per-ACL chain (TAP-bridge). Either way, a permit-only list
              blocks everything else — including return traffic from
              ESTABLISHED flows for non-matching protocols. Surface it. */}
          {onlyPermits && selectedInterfaces.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">Implicit deny at the end of the ACL</p>
                <p className="text-xs text-amber-700 mt-1">
                  Any traffic on the selected interface that does not match a permit rule above will be dropped — including return traffic for ESTABLISHED flows on protocols you did not permit.
                  {showReflect && ' For TCP/UDP, prefer "Permit (stateful)" so the reverse direction is auto-allowed.'}
                  {' '}If this ACL is bound to a WAN interface, you may lose remote access.
                </p>
              </div>
            </div>
          )}

          {/* Rules */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">Rules</label>
              <button
                type="button"
                onClick={handleAddRule}
                className="btn-secondary text-sm flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Rule
              </button>
            </div>

            <div className="space-y-4">
              {rules.map((rule, index) => (
                <div key={index} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">Rule #{index + 1}</span>
                    <div className="flex items-center space-x-1">
                      {/* VSNP-733: up/down reorder arrows. Mirrors
                          PolicyDetailsModal.tsx pattern; no DnD lib needed. */}
                      <button
                        type="button"
                        onClick={() => handleMoveRule(index, 'up')}
                        disabled={index === 0}
                        title="Move up"
                        className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-gray-400"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveRule(index, 'down')}
                        disabled={index === rules.length - 1}
                        title="Move down"
                        className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-gray-400"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                      {rules.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveRule(index)}
                          title="Delete rule"
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {/* Action */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Action</label>
                      <select
                        value={rule.act}
                        onChange={(e) => handleRuleChange(index, 'act', e.target.value as ACLAct)}
                        className="input-base w-full text-sm"
                      >
                        <option value="permit">Permit</option>
                        {showReflect && (
                          <option value="permit_reflect">Permit (stateful)</option>
                        )}
                        <option value="deny">Deny</option>
                      </select>
                      {rule.act === 'permit_reflect' && (
                        <p className="text-[10px] text-gray-500 mt-1">VPP tracks the session; return traffic is auto-permitted.</p>
                      )}
                    </div>

                    {/* Protocol */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Protocol</label>
                      <select
                        value={rule.protocol}
                        onChange={(e) => handleRuleChange(index, 'protocol', parseInt(e.target.value))}
                        className="input-base w-full text-sm"
                      >
                        {PROTOCOLS.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Source IP */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs text-gray-500">Source IP</label>
                        {/* VSNP-733: Use my IP — fetches GET /api/v2/whoami */}
                        <button
                          type="button"
                          onClick={() => fetchAndFillMyIp(index)}
                          className="text-[10px] text-blue-600 underline"
                        >
                          Use my IP
                        </button>
                      </div>
                      <input
                        type="text"
                        value={rule.sip}
                        onChange={(e) => handleRuleChange(index, 'sip', e.target.value)}
                        className={`input-base w-full text-sm ${errors[`rule.${index}.sip`] ? 'border-red-300 focus:ring-red-500' : ''}`}
                        placeholder="0.0.0.0 (any)"
                      />
                      {errors[`rule.${index}.sip`] && (
                        <p className="text-xs text-red-600 flex items-center mt-1">
                          <AlertTriangle className="w-3 h-3 mr-1" />{errors[`rule.${index}.sip`]}
                        </p>
                      )}
                    </div>

                    {/* Source Mask */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Source Mask</label>
                      <input
                        type="text"
                        value={rule.sip_mask}
                        onChange={(e) => handleRuleChange(index, 'sip_mask', e.target.value)}
                        className={`input-base w-full text-sm ${errors[`rule.${index}.sip_mask`] ? 'border-red-300 focus:ring-red-500' : ''}`}
                        placeholder="32 (single host)"
                      />
                      {errors[`rule.${index}.sip_mask`] && (
                        <p className="text-xs text-red-600 flex items-center mt-1">
                          <AlertTriangle className="w-3 h-3 mr-1" />{errors[`rule.${index}.sip_mask`]}
                        </p>
                      )}
                    </div>

                    {/* Dest IP */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Dest IP</label>
                      <input
                        type="text"
                        value={rule.dip}
                        onChange={(e) => handleRuleChange(index, 'dip', e.target.value)}
                        className={`input-base w-full text-sm ${errors[`rule.${index}.dip`] ? 'border-red-300 focus:ring-red-500' : ''}`}
                        placeholder="0.0.0.0 (any)"
                      />
                      {errors[`rule.${index}.dip`] && (
                        <p className="text-xs text-red-600 flex items-center mt-1">
                          <AlertTriangle className="w-3 h-3 mr-1" />{errors[`rule.${index}.dip`]}
                        </p>
                      )}
                    </div>

                    {/* Dest Mask */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Dest Mask</label>
                      <input
                        type="text"
                        value={rule.dip_mask}
                        onChange={(e) => handleRuleChange(index, 'dip_mask', e.target.value)}
                        className={`input-base w-full text-sm ${errors[`rule.${index}.dip_mask`] ? 'border-red-300 focus:ring-red-500' : ''}`}
                        placeholder="32 (single host)"
                      />
                      {errors[`rule.${index}.dip_mask`] && (
                        <p className="text-xs text-red-600 flex items-center mt-1">
                          <AlertTriangle className="w-3 h-3 mr-1" />{errors[`rule.${index}.dip_mask`]}
                        </p>
                      )}
                    </div>

                    {/* Source Port */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Source Port</label>
                      <input
                        type="text"
                        value={rule.sport}
                        onChange={(e) => handleRuleChange(index, 'sport', e.target.value)}
                        className={`input-base w-full text-sm ${errors[`rule.${index}.sport`] ? 'border-red-300 focus:ring-red-500' : ''}`}
                        placeholder="* (any)"
                        disabled={rule.protocol !== 6 && rule.protocol !== 17}
                      />
                      {errors[`rule.${index}.sport`] && (
                        <p className="text-xs text-red-600 flex items-center mt-1">
                          <AlertTriangle className="w-3 h-3 mr-1" />{errors[`rule.${index}.sport`]}
                        </p>
                      )}
                    </div>

                    {/* Dest Port */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Dest Port</label>
                      <input
                        type="text"
                        value={rule.dport}
                        onChange={(e) => handleRuleChange(index, 'dport', e.target.value)}
                        className={`input-base w-full text-sm ${errors[`rule.${index}.dport`] ? 'border-red-300 focus:ring-red-500' : ''}`}
                        placeholder="* (any)"
                        disabled={rule.protocol !== 6 && rule.protocol !== 17}
                      />
                      {errors[`rule.${index}.dport`] && (
                        <p className="text-xs text-red-600 flex items-center mt-1">
                          <AlertTriangle className="w-3 h-3 mr-1" />{errors[`rule.${index}.dport`]}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 px-6 py-4 border-t bg-gray-50">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSave}
            // VSNP-733: block save while interfaces failed to load — we
            // refuse to silently submit with the old [G0,G1,G2] fallback.
            disabled={saving || interfacesLoadError !== null}
            className="btn-primary"
          >
            {saving ? 'Saving...' : existingAcl ? 'Update ACL' : 'Create ACL'}
          </button>
        </div>
      </div>

      {/* VSNP-733: stylised confirm for the lock-yourself-out scenario. */}
      <ConfirmDialog
        isOpen={denyOnWanConfirmOpen}
        onClose={() => setDenyOnWanConfirmOpen(false)}
        onConfirm={async () => {
          setDenyOnWanConfirmOpen(false);
          await submitSave();
        }}
        title="Apply DENY rule on WAN interface?"
        message="If your management traffic does not match an explicit PERMIT above the deny rule, you will lose remote access to the edge. Continue?"
        confirmText="Apply ACL"
        variant="danger"
        isLoading={saving}
      />
    </div>
  );
};

export default ACLModal;
